import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Gauge, Users, ShieldAlert, ChevronDown, ChevronUp, Info,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

// ── source badge labels ───────────────────────────────────────────────────────
const SOURCE_LABELS = {
  claimed:           { label: 'Vendor-claimed', color: 'text-cyber-yellow' },
  roi_estimate:      { label: 'ROI-derived',    color: 'text-laser-green' },
  category_baseline: { label: 'Category avg',   color: 'text-text-muted' },
  review_count_proxy:{ label: 'Review signal',  color: 'text-blue-400' },
  ranking_proxy:     { label: 'Rank-derived',   color: 'text-text-muted' },
  category_estimate: { label: 'Category est.',  color: 'text-text-muted' },
};

const SourceBadge = ({ source }) => {
  const cfg = SOURCE_LABELS[source] || { label: 'Estimated', color: 'text-text-muted' };
  return (
    <span className={`text-[9px] uppercase tracking-wider font-mono ${cfg.color} opacity-80`}>
      {cfg.label}
    </span>
  );
};

const IMAGE_EXT_RE = /\.(png|jpg|jpeg|webp|gif|svg|avif)(\?|$)/i;

const isImageUrl = (value = '') => {
  const text = String(value || '').trim();
  if (!text) return false;
  if (IMAGE_EXT_RE.test(text)) return true;
  return /(image|img|cdn|cloudinary|hero|cover|screenshot|thumbnail)/i.test(text) && /^https?:\/\//i.test(text);
};

const collectSourceJsonImageUrls = (payload, output = []) => {
  if (!payload) return output;
  if (typeof payload === 'string') {
    if (isImageUrl(payload)) output.push(payload.trim());
    return output;
  }
  if (Array.isArray(payload)) {
    payload.forEach((item) => collectSourceJsonImageUrls(item, output));
    return output;
  }
  if (typeof payload === 'object') {
    Object.entries(payload).forEach(([key, value]) => {
      const keyHint = /(logo|image|cover|hero|screenshot|thumbnail|icon|gallery)/i.test(key);
      if (keyHint && typeof value === 'string' && /^https?:\/\//i.test(value)) {
        output.push(value.trim());
      }
      collectSourceJsonImageUrls(value, output);
    });
  }
  return output;
};

const pickDecisionPreviewImage = (tool = {}) => {
  const fromSourceJson = Array.from(
    new Set(collectSourceJsonImageUrls(tool?.last_source_json || {}))
  ).find((url) => /^https?:\/\//i.test(url));

  return (
    fromSourceJson ||
    tool?.cover_image ||
    tool?.background_image ||
    tool?.gallery_images?.[0] ||
    IMG.card.dataDriven
  );
};

// ── complexity colour map ─────────────────────────────────────────────────────
const COMPLEXITY_COLORS = {
  Low:        'text-laser-green shadow-[0_0_18px_rgba(57,255,20,0.22)]',
  Medium:     'text-cyber-yellow shadow-[0_0_18px_rgba(255,232,0,0.20)]',
  High:       'text-orange-400  shadow-[0_0_18px_rgba(255,160,50,0.20)]',
  Enterprise: 'text-red-400     shadow-[0_0_18px_rgba(255,80,80,0.22)]',
};

// ── small tooltip ─────────────────────────────────────────────────────────────
const Tooltip = ({ text, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center cursor-help"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.span
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50
                       w-48 text-xs text-white bg-[#0E1628] border border-white/20
                       rounded px-3 py-2 leading-relaxed shadow-xl pointer-events-none"
          >
            {text}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
};

// ── main component ────────────────────────────────────────────────────────────
const ToolPrimingLayer = ({ tool }) => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const ps = tool?.priming_signals || {};

  const timeSavingRate  = ps.time_saving_rate  ?? tool?.time_saving_rate  ?? 0;
  const timeSavingBasis = ps.time_saving_basis  ?? '';
  const timeSavingSource= ps.time_saving_source ?? '';

  const adoptionRate    = ps.competitor_adoption_pct ?? tool?.competitor_adoption_pct ?? 0;
  const adoptionTier    = ps.adoption_tier   ?? '';
  const adoptionSource  = ps.adoption_source ?? '';

  const complexity      = ps.implementation_complexity ?? tool?.implementation_complexity ?? 'Unknown';
  const complexityScore = ps.complexity_score   ?? 0;
  const complexitySignals = ps.complexity_signals ?? [];

  const painPoints      = (ps.pain_points?.length ? ps.pain_points : tool?.pain_points) ?? [];

  const visualImage = useMemo(() => pickDecisionPreviewImage(tool), [tool]);
  const visualCaption = tool?.showcase_tabs?.overview?.highlights?.[0] || 'Visualize risk before selecting this tool.';

  const complexityColorClass = COMPLEXITY_COLORS[complexity] || 'text-white';

  const [painOpen, setPainOpen] = useState(false);

  return (
    <section className="container mx-auto px-6 py-6" data-testid="tool-priming-layer">
      <div
        className="relative overflow-hidden rounded-2xl"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(100deg, rgba(255,255,255,0.97) 52%, rgba(246,248,252,0.86) 100%), url(${IMG.hero.analytics})`
            : `linear-gradient(100deg, rgba(8,8,18,0.97) 52%, rgba(8,8,18,0.65) 100%), url(${IMG.hero.analytics})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          border: '1px solid var(--card-premium-border)',
          boxShadow: 'var(--card-premium-shadow)',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="absolute -right-16 -top-16 w-44 h-44 rounded-full bg-cyber-yellow/15 blur-3xl pointer-events-none" />

        <div className="relative z-10 p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">

          {/* ── LEFT COLUMN ────────────────────────────────────────────── */}
          <div className="lg:col-span-8 flex flex-col gap-4">

            <div>
              <p className="text-xs uppercase tracking-[0.16em] font-mono mb-2 text-cyber-yellow">
                Before you buy {tool?.name}
              </p>
              <h3 className="text-2xl text-white font-semibold mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Cut decision anxiety with concrete signals
              </h3>
              <p className="text-sm text-text-secondary">
                A quick snapshot of speed, market pressure, and rollout risk before you commit budget.
              </p>
            </div>

            {/* ── 3 signal cards ─────────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

              {/* Time saved */}
              <motion.div
                whileHover={{ y: -4 }}
                className="premium-tool-muted p-4 rounded-xl border shadow-[0_0_24px_rgba(57,255,20,0.13)] min-h-[100px] flex flex-col justify-between"
                data-testid="tool-prime-time-saving"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Gauge size={14} className="text-laser-green" />
                    <span className="text-[11px] tracking-[0.08em] text-text-muted uppercase">Expected time saved</span>
                  </div>
                  <Tooltip text="Derived from vendor claims on the official page, ROI hours estimate, or category benchmarks from similar tools.">
                    <Info size={12} className="text-text-muted opacity-60" />
                  </Tooltip>
                </div>
                <p className="text-3xl leading-none text-white font-semibold">
                  {timeSavingRate ? `${timeSavingRate}%` : 'TBD'}
                </p>
                <div className="flex items-center justify-between mt-2 gap-1">
                  {timeSavingBasis && (
                    <span className="text-[10px] text-text-muted truncate max-w-[120px]">{timeSavingBasis}</span>
                  )}
                  <SourceBadge source={timeSavingSource} />
                </div>
              </motion.div>

              {/* Competitor adoption */}
              <motion.div
                whileHover={{ y: -4 }}
                className="premium-tool-muted p-4 rounded-xl border shadow-[0_0_24px_rgba(255,232,0,0.14)] min-h-[100px] flex flex-col justify-between"
                data-testid="tool-prime-adoption"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-cyber-yellow" />
                    <span className="text-[11px] tracking-[0.08em] text-text-muted uppercase">Competitor adoption</span>
                  </div>
                  <Tooltip text="Based on verified review platform counts (G2, Capterra, Trustpilot). Maps to Emerging → Dominant adoption tiers.">
                    <Info size={12} className="text-text-muted opacity-60" />
                  </Tooltip>
                </div>
                <p className="text-3xl leading-none text-white font-semibold">
                  {adoptionRate ? `${adoptionRate}%` : 'TBD'}
                </p>
                <div className="flex items-center justify-between mt-2 gap-1">
                  {adoptionTier && (
                    <span className="text-[10px] text-text-muted">{adoptionTier}</span>
                  )}
                  <SourceBadge source={adoptionSource} />
                </div>
              </motion.div>

              {/* Implementation complexity */}
              <motion.div
                whileHover={{ y: -4 }}
                className="premium-tool-muted p-4 rounded-xl border min-h-[100px] flex flex-col justify-between"
                style={{ boxShadow: '0 0 24px rgba(255,120,120,0.12)' }}
                data-testid="tool-prime-complexity"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-red-400" />
                    <span className="text-[11px] tracking-[0.08em] text-text-muted uppercase">Implementation complexity</span>
                  </div>
                  <Tooltip text="Calculated from integration requirements, feature density, onboarding friction signals in reviews, and pricing tier type.">
                    <Info size={12} className="text-text-muted opacity-60" />
                  </Tooltip>
                </div>
                <p className={`text-3xl leading-none font-semibold ${complexityColorClass}`}>
                  {complexity}
                </p>
                {complexitySignals.length > 0 && (
                  <p className="text-[10px] text-text-muted mt-2 leading-snug line-clamp-2">
                    {complexitySignals[0]}
                  </p>
                )}
              </motion.div>
            </div>

            {/* ── Pain points accordion ───────────────────────────────── */}
            {painPoints.length > 0 && (
              <div
                className="premium-tool-muted rounded-xl border overflow-hidden"
                data-testid="tool-prime-pain-points"
              >
                <button
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--card-premium-muted-bg)] transition-colors"
                  onClick={() => setPainOpen(v => !v)}
                  aria-expanded={painOpen}
                >
                  <div className="flex items-center gap-2">
                    <ShieldAlert size={14} className="text-red-400 flex-shrink-0" />
                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                      Why buyers switch to {tool?.name || 'this tool'} — pain points this solves
                    </span>
                  </div>
                  {painOpen
                    ? <ChevronUp size={14} className="text-text-muted flex-shrink-0" />
                    : <ChevronDown size={14} className="text-text-muted flex-shrink-0" />}
                </button>

                <AnimatePresence initial={false}>
                  {painOpen && (
                    <motion.div
                      key="pain-points-expand"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <ul className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {painPoints.slice(0, 4).map((point, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                            <span className="text-sm text-text-secondary leading-snug">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* ── Action buttons ─────────────────────────────────────── */}
            <div className="flex flex-wrap gap-3 pt-1" data-testid="tool-prime-consulting-panel">
              <Link to="/consulting" className="btn-primary">Need a second opinion? Book consulting</Link>
              <Link to={`/alternatives/${tool?.slug}`} className="btn-secondary">Compare safer alternatives</Link>
            </div>
          </div>

          {/* ── RIGHT COLUMN — visual panel ────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="hidden lg:flex lg:col-span-4 flex-col gap-3"
            data-testid="tool-prime-visual"
          >
            <div className="flex-1 min-h-[270px] rounded-2xl border overflow-hidden relative shadow-[0_24px_48px_rgba(0,0,0,0.45)]" style={{ borderColor: 'var(--card-premium-border)', background: 'var(--card-premium-muted-bg)' }}>
              <img
                src={visualImage}
                alt={`${tool?.name || 'Tool'} decision preview`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ background: isLight ? 'linear-gradient(to top, rgba(255,255,255,0.84), rgba(255,255,255,0.08), transparent)' : 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.15), transparent)' }} />
              <div className="absolute bottom-3 left-3 right-3 p-3 backdrop-blur-sm rounded-lg" style={{ background: isLight ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.78)', border: '1px solid var(--card-premium-border)' }}>
                <p className="text-[11px] uppercase tracking-[0.16em] font-mono text-cyber-yellow">Decision preview</p>
                <p className="text-sm leading-snug" style={{ color: 'var(--text-primary)' }}>{visualCaption}</p>
              </div>
            </div>

            {/* Complexity bar */}
            {complexityScore > 0 && (
              <div className="premium-tool-muted rounded-xl border px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-text-muted">Complexity score</span>
                  <span className={`text-sm font-bold ${complexityColorClass}`}>{complexityScore}/100</span>
                </div>
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(var(--glow-1-rgb), 0.16)' }}>
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${complexityScore}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className={`h-full rounded-full ${
                      complexity === 'Low' ? 'bg-laser-green' :
                      complexity === 'Medium' ? 'bg-cyber-yellow' :
                      complexity === 'High' ? 'bg-orange-400' : 'bg-red-400'
                    }`}
                  />
                </div>
              </div>
            )}
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default ToolPrimingLayer;
